import * as React from 'react';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { VARIABLE_CATEGORY_STYLES, getVariableCategory, VariableCategory } from '../HTMLEditorQuill/VariableBlot';

interface Variable {
  label: string;
  value: string;
}

const CHILD_VARIABLES: Variable[] = [
  { label: 'First name', value: '{{childFirstName}}' },
  { label: 'Last name', value: '{{childLastName}}' },
  { label: 'Full name', value: '{{childFullName}}' },
  { label: 'Form', value: '{{childForm}}' },
  { label: 'Year group', value: '{{childYearGroup}}' },
  { label: 'House', value: '{{childHouse}}' }
];

const RECIPIENT_VARIABLES: Variable[] = [
  { label: 'First name', value: '{{recipientFirstName}}' },
  { label: 'Last name', value: '{{recipientLastName}}' },
  { label: 'Full name', value: '{{recipientFullName}}' }
];

const SCHOOL_VARIABLES: Variable[] = [
  { label: 'School name', value: '{{schoolName}}' },
  { label: 'School logo', value: '{{schoolLogo}}' }
];

const SENDER_VARIABLES: Variable[] = [{ label: 'Sender name', value: '{{senderName}}' }];

const OTHER_VARIABLES: Variable[] = [
  { label: 'Current year', value: '{{currentYear}}' },
  { label: 'Current date', value: '{{currentDate}}' }
];

interface VariableChipProps {
  variable: Variable;
  onInsert: (value: string) => void;
}

const VariableChip: FunctionComponent<VariableChipProps> = ({ variable, onInsert }) => {
  const category = getVariableCategory(variable.value);
  const styles = VARIABLE_CATEGORY_STYLES[category];

  return (
    <button
      type="button"
      className="gmVarChip"
      title={`Insert ${variable.value}`}
      onClick={() => onInsert(variable.value)}
      style={{ backgroundColor: styles.bg, color: styles.text, borderColor: styles.border }}
    >
      {variable.label}
    </button>
  );
};

interface VariableGroupProps {
  category: VariableCategory;
  variables: Variable[];
  onInsert: (value: string) => void;
}

const VariableGroup: FunctionComponent<VariableGroupProps> = ({ category, variables, onInsert }) => {
  const styles = VARIABLE_CATEGORY_STYLES[category];

  if (variables.length === 0) {
    return null;
  }

  return (
    <div className="gmVarGroup">
      <div className="gmVarGroup__title" style={{ color: styles.text }}>{styles.title}</div>
      <div className="gmVarGroup__chips">
        {variables.map(variable => (
          <VariableChip key={variable.value} variable={variable} onInsert={onInsert} />
        ))}
      </div>
    </div>
  );
};

interface Props {
  onInsert: (value: string) => void;
  showStudentVariables?: boolean;
  showRecipientVariables?: boolean;
  compact?: boolean;
  excludeVariables?: string[];
}

export const GeneralMessageVariableInsert: FunctionComponent<Props> = ({
  onInsert,
  showStudentVariables = true,
  showRecipientVariables = true,
  excludeVariables = []
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isExpanded]);

  const filter = (variables: Variable[]) => variables.filter(v => excludeVariables.indexOf(v.value) === -1);
  const insert = (value: string) => {
    onInsert(value.endsWith(' ') ? value : `${value} `);
    setIsExpanded(false);
  };

  return (
    <div className="gmVarInsert" ref={rootRef}>
      <button type="button" className="gmVarInsert__button" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="gmVarInsert__hash">#</span>
        Insert variable
      </button>

      {isExpanded && (
        <div className="gmVarInsert__panel">
          <div className="gmVarInsert__hint">Click a variable to insert it at the cursor.</div>
          {showStudentVariables && <VariableGroup category="child" variables={filter(CHILD_VARIABLES)} onInsert={insert} />}
          {showRecipientVariables && <VariableGroup category="recipient" variables={filter(RECIPIENT_VARIABLES)} onInsert={insert} />}
          <VariableGroup category="school" variables={filter(SCHOOL_VARIABLES)} onInsert={insert} />
          <VariableGroup category="sender" variables={filter(SENDER_VARIABLES)} onInsert={insert} />
          <VariableGroup category="other" variables={filter(OTHER_VARIABLES)} onInsert={insert} />
        </div>
      )}
    </div>
  );
};

GeneralMessageVariableInsert.displayName = 'GeneralMessageVariableInsert';
